/**
 * Agreement Page - דף חתימת הסכם
 * Client must sign the advisory agreement before accessing the platform
 */

import { useState, useEffect } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { useAuthStore } from '../store/authStore'
import api from '../services/api'
import LegalConsentModal from '../components/legal/LegalConsentModal'
import SignaturePad from '../components/signature/SignaturePad'

// Israeli national ID (תעודת זהות) check-digit validation — mirrors the backend validator
function isValidIsraeliId(id: string): boolean {
  if (!/^\d{9}$/.test(id)) return false
  const digits = id.split('').map(Number)
  let sum = 0
  for (let i = 0; i < 9; i++) {
    const num = digits[i] * ((i % 2) + 1)
    sum += num > 9 ? num - 9 : num
  }
  return sum % 10 === 0
}

// ── Main Page ────────────────────────────────────────────────────────────────
export default function AgreementPage() {
  const navigate = useNavigate()
  const { isAuthenticated, user, updateUser } = useAuthStore()

  const [signatureDataUrl, setSignatureDataUrl] = useState('')
  const [signatureEmpty, setSignatureEmpty] = useState(true)
  const [typedName, setTypedName] = useState('')
  const [nationalId, setNationalId] = useState('')
  const [address, setAddress] = useState('')
  const [confirmed, setConfirmed] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState('')
  // Live (admin-editable) contract text, fetched from the backend
  const [contractTitle, setContractTitle] = useState('הסכם ייעוץ השקעות')
  const [contractBodyHtml, setContractBodyHtml] = useState('')

  // Post-signing legal-documents consent (privacy / terms / accessibility).
  // `null` = not resolved yet; it is resolved from the backend on mount so a
  // client who signed but closed the tab before confirming is asked again.
  const [needsLegalConsent, setNeedsLegalConsent] = useState<boolean | null>(null)
  const [consentSubmitting, setConsentSubmitting] = useState(false)
  const [consentError, setConsentError] = useState('')

  // Live national-ID validation, so an invalid ID explains itself instead of just disabling the button
  const idTrimmed = nationalId.trim()
  const nationalIdValid = isValidIsraeliId(idTrimmed)
  const nationalIdError = idTrimmed.length === 0
    ? ''
    : idTrimmed.length < 9
      ? 'תעודת זהות חייבת להכיל 9 ספרות'
      : !nationalIdValid
        ? 'תעודת זהות אינה תקינה'
        : ''

  // Sync agreement status from backend on mount + pre-fill ID if given at registration
  useEffect(() => {
    if (!isAuthenticated || !user) return
    api.get('/agreement').then((res) => {
      const outstandingConsent = !res.data?.legalDocsAccepted
      if (res.data?.agreementSigned) {
        // The store not knowing about the signature means it happened elsewhere
        // (another tab / a previous session that never synced) — that user still
        // belongs in the questionnaire. A user the store already knows is signed
        // is a returning visitor, and the guard below sends them to /dashboard.
        const justSynced = !user.agreementSigned
        updateUser({ agreementSigned: true })
        setNeedsLegalConsent(outstandingConsent)
        // Only move on once the legal documents have been confirmed.
        if (!outstandingConsent && justSynced) navigate('/questionnaire', { replace: true })
        return
      }
      setNeedsLegalConsent(false)
      if (res.data?.nationalId) {
        setNationalId(res.data.nationalId)
      }
      if (res.data?.contractTitle) setContractTitle(res.data.contractTitle)
      if (res.data?.contractBodyHtml) setContractBodyHtml(res.data.contractBodyHtml)
    }).catch(() => {
      // Backend unreachable — don't strand an already-signed user on a blank page.
      setNeedsLegalConsent(false)
    })
  }, [])

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />
  }
  // Already-signed users leave this page, but only after we know whether the
  // legal-documents consent is still outstanding.
  if (user.agreementSigned && needsLegalConsent === null) {
    return (
      <div dir="rtl" className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-gray-500">טוען…</p>
      </div>
    )
  }
  if (user.agreementSigned && needsLegalConsent === false) {
    return <Navigate to="/dashboard" replace />
  }

  // Records the consent, then continues into the questionnaire.
  const handleLegalConsent = async () => {
    setConsentError('')
    setConsentSubmitting(true)
    try {
      await api.post('/agreement/legal-consent', { accepted: true })
      // Navigate only — clearing the flag here would briefly satisfy the
      // "signed + consented" guard above and flash a redirect to /dashboard.
      navigate('/questionnaire', { replace: true })
    } catch (err: any) {
      setConsentError(err.response?.data?.error || 'שגיאה בשמירת האישור. אנא נסה שוב.')
    } finally {
      setConsentSubmitting(false)
    }
  }

  const handleSign = async () => {
    if (!typedName.trim()) {
      setError('יש להזין שם מלא')
      return
    }
    if (signatureEmpty && !typedName.trim()) {
      setError('יש לחתום בתיבת החתימה או להזין שם מלא כחלופה')
      return
    }
    if (!nationalIdValid) {
      setError(nationalIdError || 'יש להזין תעודת זהות תקינה')
      return
    }
    if (!address.trim()) {
      setError('יש להזין כתובת')
      return
    }
    if (!confirmed) {
      setError('יש לאשר קריאת ההסכם')
      return
    }
    setError('')
    setIsSubmitting(true)
    try {
      const res = await api.post('/agreement/sign', {
        signature: typedName.trim(),
        signatureImage: signatureDataUrl,
        confirmed,
        nationalId: nationalId.trim(),
        address: address.trim(),
      })
      updateUser({ agreementSigned: true, agreementSignedAt: res.data.agreementSignedAt })
      // Gate the questionnaire behind confirming the legal documents.
      setNeedsLegalConsent(true)
    } catch (err: any) {
      // If already signed (409), treat as success — but ask the backend whether
      // the legal documents were already confirmed before re-prompting.
      if (err.response?.status === 409) {
        updateUser({ agreementSigned: true })
        const alreadyAccepted = await api
          .get('/agreement')
          .then((r) => !!r.data?.legalDocsAccepted)
          .catch(() => false)
        if (alreadyAccepted) {
          navigate('/questionnaire', { replace: true })
        } else {
          setNeedsLegalConsent(true)
        }
        return
      }
      setError(err.response?.data?.error || 'שגיאה בחתימה. אנא נסה שוב.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const today = new Date().toLocaleDateString('he-IL', {
    year: 'numeric', month: 'long', day: 'numeric',
  })

  // Signed, but the legal documents still need confirming — show only the gate.
  if (user.agreementSigned && needsLegalConsent) {
    return (
      <div dir="rtl" className="min-h-screen bg-gray-50">
        <LegalConsentModal
          onConfirm={handleLegalConsent}
          isSubmitting={consentSubmitting}
          error={consentError}
        />
      </div>
    )
  }

  return (
    <div dir="rtl" className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">

        {/* Header */}
        <div className="bg-blue-900 text-white rounded-t-lg p-6 text-center">
          <h1 className="text-2xl font-bold">יפתח ונגר יעוץ השקעות ופנסיוני אובייקטיבי</h1>
          <p className="text-blue-200 text-sm mt-1">יפתח ונגר — יועץ השקעות ויועץ פנסיוני אובייקטיבי</p>
          <p className="text-blue-200 text-sm">ברישיון רשות ני"ע ורשות שוק ההון</p>
        </div>

        <div className="bg-white shadow rounded-b-lg p-8">
          <h2 className="text-xl font-bold text-center text-gray-800 mb-2">{contractTitle}</h2>
          <p className="text-center text-gray-500 text-sm mb-6">שנערך ונחתם בתאריך: {today}</p>

          {/* Parties */}
          <div className="grid grid-cols-2 gap-6 mb-6 text-sm border border-gray-200 rounded-lg p-4">
            <div>
              <p className="font-semibold text-gray-700 mb-1">היועץ:</p>
              <p>יפתח ונגר, יועץ השקעות ויועץ פנסיוני</p>
              <p>טלפון: 050-3976397</p>
              <p>מייל: iftach.venger@gmail.com</p>
            </div>
            <div>
              <p className="font-semibold text-gray-700 mb-1">הלקוח:</p>
              <p>{user?.firstName} {user?.lastName}</p>
              {nationalId.trim() && <p>ת.ז: {nationalId.trim()}</p>}
              {address.trim() && <p>מרחוב: {address.trim()}</p>}
              <p>דוא"ל: {user?.email}</p>
            </div>
          </div>

          {/* Agreement text (admin-editable — fetched from the backend) */}
          <style>{`
            .agreement-render { direction: rtl; }
            .agreement-render h2 { font-size: 15px; font-weight: 700; color: #111827; margin: 14px 0 6px; }
            .agreement-render .section { margin-bottom: 12px; }
            .agreement-render .clause { margin-bottom: 5px; }
            .agreement-render .preamble p { margin-bottom: 4px; }
            .agreement-render strong { font-weight: 700; }
            .agreement-render ul, .agreement-render ol { padding-inline-start: 22px; margin: 5px 0; }
            .agreement-render .def-table { width: 100%; border-collapse: collapse; margin: 6px 0 12px; }
            .agreement-render .def-table td { vertical-align: top; padding: 4px 8px; border-bottom: 1px solid #eee; }
            .agreement-render .def-table td:first-child { font-weight: 700; width: 140px; white-space: nowrap; }
          `}</style>
          <div className="agreement-render text-sm text-gray-700 leading-relaxed max-h-96 overflow-y-auto border border-gray-100 rounded-lg p-4 bg-gray-50">
            {contractBodyHtml
              ? <div dangerouslySetInnerHTML={{ __html: contractBodyHtml }} />
              : <p className="text-gray-400 text-center py-8">טוען את נוסח ההסכם…</p>}
          </div>

          {/* Signature Section */}
          <div className="mt-8 border-t pt-6 space-y-5">
            <h3 className="font-bold text-gray-800 text-lg">חתימה דיגיטלית</h3>

            {/* Confirmation checkbox */}
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="confirmed"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                className="mt-1 w-4 h-4 cursor-pointer"
              />
              <label htmlFor="confirmed" className="text-sm text-gray-700 cursor-pointer">
                קראתי והבנתי את ההסכם במלואו, ואני מסכים/ה לכל תנאיו
              </label>
            </div>

            {/* Client details required for the contract */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="nationalId" className="block text-sm font-medium text-gray-700 mb-1">
                  תעודת זהות <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="nationalId"
                  inputMode="numeric"
                  maxLength={9}
                  dir="ltr"
                  value={nationalId}
                  onChange={(e) => setNationalId(e.target.value.replace(/\D/g, ''))}
                  placeholder="9 ספרות"
                  className={`w-full border rounded-lg px-4 py-3 text-base text-right focus:outline-none focus:ring-2 ${
                    nationalIdError ? 'border-red-400 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'
                  }`}
                  aria-required="true"
                  aria-invalid={!!nationalIdError}
                  aria-describedby={nationalIdError ? 'nationalId-error' : undefined}
                />
                {nationalIdError && (
                  <p id="nationalId-error" role="alert" className="text-red-600 text-xs mt-1">{nationalIdError}</p>
                )}
              </div>
              <div>
                <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">
                  כתובת <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="address"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="רחוב, מספר, עיר"
                  maxLength={200}
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
                  aria-required="true"
                  aria-invalid={!!error && !address.trim()}
                />
              </div>
            </div>

            {/* Canvas signature pad */}
            <div>
              <span id="signature-canvas-label" className="block text-sm font-medium text-gray-700 mb-2">
                חתימה בכתב יד (לא חובה)
              </span>
              <SignaturePad
                onSignatureChange={setSignatureDataUrl}
                isEmpty={signatureEmpty}
                setIsEmpty={setSignatureEmpty}
              />
            </div>

            {/* Typed name — accessible alternative to drawing */}
            <div>
              <label htmlFor="typedName" className="block text-sm font-medium text-gray-700 mb-1">
                שם מלא (חלופה לחתימה) <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="typedName"
                value={typedName}
                onChange={(e) => setTypedName(e.target.value)}
                placeholder="הזן/י שם מלא"
                className="w-full border border-gray-300 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{ fontFamily: 'cursive' }}
                aria-required="true"
                aria-invalid={!!error && !typedName.trim()}
                aria-describedby={error ? 'agreement-error' : undefined}
              />
            </div>

            {error && <p id="agreement-error" role="alert" className="text-red-600 text-sm">{error}</p>}

            <button
              onClick={handleSign}
              disabled={isSubmitting || !typedName.trim() || !confirmed || !nationalIdValid || !address.trim()}
              className="w-full bg-blue-900 text-white py-3 rounded-lg font-semibold text-base hover:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? 'שולח...' : 'חתום על ההסכם'}
            </button>

            <p className="text-xs text-gray-400 text-center">
              החתימה הדיגיטלית תישמר עם חותמת זמן וכתובת IP לצרכי תיעוד משפטי
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
